import { useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * useLifecycleActions — small hook that wires the confirm dialog + API calls
 * for the standard Deactivate / Reactivate / Delete / Restore flow.
 *
 * Usage:
 *   const lc = useLifecycleActions({
 *     basePath: "/admin/coupons",     // no leading /api — matches your `api` client
 *     labelOf: (row) => `"${row.coupon_code}"`,
 *     onDone: reload,
 *   });
 *   ...
 *   <RowActionsMenu
 *     status={row.status}
 *     onDeactivate={() => lc.open("deactivate", row)}
 *     onReactivate={() => lc.open("reactivate", row)}
 *     onDelete={() => lc.open("delete", row)}
 *     onRestore={() => lc.open("restore", row)}
 *   />
 *   {lc.dialog}
 */
import { LifecycleConfirmDialog } from "@/components/admin/LifecycleConfirmDialog";

export function useLifecycleActions({ basePath, labelOf, onDone, successMessages = {} }) {
  const [state, setState] = useState({ action: null, row: null, open: false });
  const [loading, setLoading] = useState(false);

  const open = useCallback((action, row) => {
    setState({ action, row, open: true });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const confirm = useCallback(async (reason) => {
    const { action, row } = state;
    if (!action || !row?.id) return;
    setLoading(true);
    try {
      const body = { reason: reason || "" };
      if (action === "delete") {
        await api.delete(`${basePath}/${row.id}`, { data: body });
      } else if (action === "deactivate") {
        await api.patch(`${basePath}/${row.id}/deactivate`, body);
      } else if (action === "reactivate") {
        await api.patch(`${basePath}/${row.id}/reactivate`, body);
      } else if (action === "restore") {
        await api.patch(`${basePath}/${row.id}/restore`, {});
      }
      const msg = successMessages[action] || {
        delete: "Record deleted successfully and removed from active list.",
        deactivate: "Record deactivated successfully.",
        reactivate: "Record reactivated successfully.",
        restore: "Record restored successfully.",
      }[action];
      toast.success(msg);
      setState({ action: null, row: null, open: false });
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [state, basePath, onDone, successMessages]);

  const label = state.row && labelOf ? labelOf(state.row) : "this record";

  const dialog = (
    <LifecycleConfirmDialog
      open={state.open}
      onOpenChange={(o) => (o ? null : close())}
      action={state.action}
      recordLabel={label}
      loading={loading}
      onConfirm={confirm}
    />
  );

  return { open, close, dialog, loading, currentAction: state.action, currentRow: state.row };
}
